import React from 'react';
import { Link } from 'react-router-dom';
import { PiArrowUpRightLight, PiArrowRightLight } from 'react-icons/pi';

type Variant = 'solid' | 'ghost' | 'light';

interface CtaProps {
  children: React.ReactNode;
  to?: string;
  href?: string;
  variant?: Variant;
  /** Diagonal arrow for outbound, straight arrow for in-app navigation. */
  external?: boolean;
  className?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
}

const shell: Record<Variant, string> = {
  solid: 'bg-espresso-800 text-cream hover:bg-espresso-900',
  ghost: 'bg-transparent text-espresso-800 ring-1 ring-inset ring-espresso-700/15 hover:bg-espresso-700/[0.04]',
  light: 'bg-cream text-espresso-800 hover:bg-white',
};

const puck: Record<Variant, string> = {
  solid: 'bg-white/10',
  ghost: 'bg-espresso-700/[0.06]',
  light: 'bg-espresso-700/[0.06]',
};

/**
 * Island CTA: a fully-rounded pill whose trailing arrow lives inside its own
 * circular puck, flush with the pill's right inner padding. On hover the puck
 * drifts diagonally and scales while the whole pill presses down — internal
 * kinetic tension rather than a flat colour swap.
 */
const Cta: React.FC<CtaProps> = ({
  children,
  to,
  href,
  variant = 'solid',
  external = false,
  className = '',
  type = 'button',
  disabled = false,
}) => {
  const Arrow = external ? PiArrowUpRightLight : PiArrowRightLight;

  const body = (
    <>
      <span className="relative z-10">{children}</span>
      <span
        aria-hidden="true"
        className={`relative z-10 -mr-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${puck[variant]} transition-transform duration-700 ease-fluid group-hover:translate-x-1 group-hover:-translate-y-px group-hover:scale-105`}
      >
        <Arrow className="h-4 w-4" strokeWidth={1} />
      </span>
    </>
  );

  const base = `group inline-flex items-center gap-3 rounded-full px-6 py-3 text-sm font-medium tracking-tight
    transition-all duration-700 ease-fluid active:scale-[0.98]
    disabled:pointer-events-none disabled:opacity-50 ${shell[variant]} ${className}`;

  if (to) {
    return (
      <Link to={to} className={base}>
        {body}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={base}>
        {body}
      </a>
    );
  }

  return (
    <button type={type} disabled={disabled} className={base}>
      {body}
    </button>
  );
};

export default Cta;
